import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import Watermark from "../components/Watermark";
import { toast } from "react-hot-toast";
import StatusFilterCards from "../components/StatusFilterCards";
import useServerEvents from "../hooks/useServerEvents";
import { getMinDate, validateDueDate, parseLocalDate } from "../utils/dateUtils";
import { buildOverallWorkbook } from "../utils/exportUtils";

const WORKFLOW_TABS = ["Org Tasks", "Management Requests"];
const PRIORITIES = ["Low", "Medium", "High"];
const SORT_OPTIONS = [
  { label: "Task ID Asc", value: "id_asc" },
  { label: "Task ID Desc", value: "id_desc" },
  { label: "Due Date Asc", value: "due_asc" },
  { label: "Due Date Desc", value: "due_desc" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [sort, setSort] = useState("id_desc");
  // Default to "open" filter on both Org Tasks and Management Requests
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [usersMap, setUsersMap] = useState({});
  // Restore workflow tab from sessionStorage so "Back to Dashboard" from a
  // Management Request task returns to the Management Requests tab, not Org Tasks.
  const [workflowTab, setWorkflowTab] = useState(
    () => sessionStorage.getItem("admin_workflow_tab") || "Org Tasks"
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "" });
  const [createMsg, setCreateMsg] = useState("");
  const [taskCreating, setTaskCreating] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  // ── Overall Export modal state ─────────────────────────────────────────────
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFrom, setExportFrom]           = useState("");
  const [exportTo, setExportTo]               = useState("");
  const [exporting, setExporting]             = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("jwt");

  function addBusinessDays(date, days) {
    let result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      // 0 = Sunday, 6 = Saturday
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        added++;
      }
    }
    return result;
  }

    // Fetch users map (now returns the map)
  const fetchUsersMap = async () => {
    try {
      const res = await axios.get(`${apiUrl}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      (res.data || []).forEach((u) => {
        map[u.id] = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      });
      setUsersMap(map);
      return map; // return map so caller can use immediately
    } catch (err) {
      return {};
    }
  };

  const handleCreateAdminTask = async (e) => {
    e.preventDefault();
    if (!newTask.title || !newTask.description) return setCreateMsg("Title and description are required.");
    const due_date = newTask.due_date || null;
    const dateErr = validateDueDate(due_date);
    if (dateErr) return setCreateMsg(dateErr);
    setTaskCreating(true);
    try {
      const payload = { ...newTask, due_date };
      if (!payload.assigned_to) delete payload.assigned_to;
      await axios.post(`${apiUrl}/tasks`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Task created successfully!");
      setNewTask({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "" });
      setCreateMsg("");
      setAssigneeSearch("");
      setShowCreateForm(false);
      fetchTasks();
    } catch (err) {
      setCreateMsg(err.response?.data?.error || "Error creating task.");
    } finally {
      setTaskCreating(false);
    }
  };

  // Fetch tasks (accept optional usersMap param to avoid stale state)
  const fetchTasks = async (usersMapParam = null) => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // sort by created_at descending (newest first). fallback to id desc
      const getTime = (t) => {
        if (t.created_at) return new Date(t.created_at).getTime();
        return t.id ? Number(t.id) : 0;
      };
      const sorted = (res.data || []).slice().sort((a, b) => getTime(b) - getTime(a));

      const mapToUse = usersMapParam || usersMap || {};
      const withNames = sorted.map((t) => ({
        ...t,
        created_by_name: mapToUse[t.created_by] || null,
      }));

      setTasks(withNames);
    } catch (err) {
      // handle error if needed
    } finally {
      setLoading(false);
    }
  };

  // bootstrap: load users map first, then tasks; keep auto-refresh for tasks only
  useEffect(() => {
    let interval;
    (async () => {
      const map = await fetchUsersMap();
      await fetchTasks(map); // pass the fresh map to avoid stale state
      interval = setInterval(() => fetchTasks(), 30 * 1000);
    })();
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  useServerEvents({
    onTaskCreated: () => fetchTasks(),
    onTaskUpdated: () => fetchTasks(),
  }, token);

  useEffect(() => {
    // Fetch all users except superusers (role_id 3) for the assign dropdown
    axios.get(`${apiUrl}/users`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const all = res.data || [];
      setEmployeeList(all.filter(u => Number(u.role_id) !== 3));
    }).catch(() => {});
  }, []);

  // Filter and sort tasks
  const todayAdmin = new Date(); todayAdmin.setHours(0, 0, 0, 0);

  // Split by workflow type using task_type_code (stable code, not display name)
  const workflowTasks = workflowTab === "Management Requests"
    ? tasks.filter((t) => t.task_type_code === "ADMIN_REQUEST")
    : tasks.filter((t) => t.task_type_code === "ADMIN_TASK");

  // When a status filter card is active, work from all workflow tasks.
  // No tab split — status filter cards handle the segmentation.
  let filteredTasks = statusFilter ? workflowTasks : workflowTasks;

  // Stats are always computed across ALL workflow tasks (regardless of Current/Closed tab)
  // so the numbers are stable and consistent no matter which tab is active.
  const isClosed = (t) => t.status === "Closed";
  const isNotClosedStat = (t) => !isClosed(t);
  const adminStats = [
    { label: "Open",      value: workflowTasks.filter(t => t.status === "Open").length,    accent: 'bg-blue-500',   icon: '📋', filter: "open" },
    { label: "Ongoing",   value: workflowTasks.filter(t => t.status === "Ongoing").length, accent: 'bg-yellow-400', icon: '⚡', filter: "in_progress" },
    { label: "Blocked",   value: workflowTasks.filter(t => t.status === "Blocked").length, accent: 'bg-red-500',    icon: '🚫', filter: "blocked" },
    { label: "On Hold",   value: workflowTasks.filter(t => t.status === "On Hold").length, accent: 'bg-orange-400', icon: '⏸', filter: "on_hold" },
    { label: "Closed",    value: workflowTasks.filter(t => isClosed(t)).length,            accent: 'bg-green-500',  icon: '✅', filter: "closed" },
    { label: "Due Today", value: workflowTasks.filter(t => t.due_date && parseLocalDate(t.due_date).toDateString() === todayAdmin.toDateString() && isNotClosedStat(t)).length, accent: 'bg-amber-400', icon: '📅', filter: "due_today" },
    { label: "Overdue",   value: workflowTasks.filter(t => t.due_date && parseLocalDate(t.due_date) < todayAdmin && isNotClosedStat(t)).length, accent: 'bg-red-700', icon: '⚠️', filter: "overdue" },
  ];

  // Status card / dropdown filter — map legacy keys to Title Case API values
  const STATUS_MAP_ADMIN = { open: "Open", in_progress: "Ongoing", blocked: "Blocked", on_hold: "On Hold", closed: "Closed" };
  if (statusFilter) {
    if (statusFilter === "due_today") {
      filteredTasks = filteredTasks.filter(t => t.due_date && parseLocalDate(t.due_date).toDateString() === todayAdmin.toDateString() && t.status !== 'Closed');
    } else if (statusFilter === "overdue") {
      filteredTasks = filteredTasks.filter(t => t.due_date && parseLocalDate(t.due_date) < todayAdmin && t.status !== 'Closed');
    } else {
      const apiStatus = STATUS_MAP_ADMIN[statusFilter] || statusFilter;
      filteredTasks = filteredTasks.filter(t => t.status === apiStatus);
    }
  }

  // Search
  const q = (search || "").toString().trim().toLowerCase();
  if (q) {
    filteredTasks = filteredTasks.filter((t) => {
      const idStr = (t.id !== undefined && t.id !== null) ? t.id.toString() : "";
      return (
        idStr.toLowerCase().includes(q) ||
        (t.title || "").toString().toLowerCase().includes(q)
      );
    });
  }

  // Sorting
  if (sort === "id_asc") filteredTasks.sort((a, b) => a.id - b.id);
  if (sort === "id_desc") filteredTasks.sort((a, b) => b.id - a.id);
  if (sort === "due_asc") filteredTasks.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  if (sort === "due_desc") filteredTasks.sort((a, b) => new Date(b.due_date) - new Date(a.due_date));

  // Pagination
  const totalPages = Math.ceil(filteredTasks.length / rowsPerPage);
  const paginatedTasks = filteredTasks.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  
  // Export to Excel — opens date-range modal
  const handleExport = () => {
    setExportFrom("");
    setExportTo("");
    setShowExportModal(true);
  };

  const handleRunExport = async () => {
    if (!exportFrom || !exportTo) {
      toast.error("Please select both a From and To date");
      return;
    }
    if (new Date(exportFrom) > new Date(exportTo)) {
      toast.error('"From" date cannot be after "To" date');
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(
        `${apiUrl}/tasks/export?from=${exportFrom}&to=${exportTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (${res.status})`);
      }
      const bundle = await res.json();
      if (!bundle.tasks || bundle.tasks.length === 0) {
        toast.error("No tasks found for the selected period");
        setExporting(false);
        return;
      }
      const wb = buildOverallWorkbook(bundle);
      const today = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Tasks_Export_${exportFrom}_to_${exportTo}_${today}.xlsx`);
      toast.success(`Exported ${bundle.tasks.length} task${bundle.tasks.length !== 1 ? "s" : ""} to Excel`);
      setShowExportModal(false);
    } catch (err) {
      toast.error(err.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Group tasks by month
  function groupByMonth(tasks) {
    return tasks.reduce((acc, task) => {
      const month = task.closed_at
        ? new Date(task.closed_at).toLocaleString("default", { month: "long", year: "numeric" })
        : "Unknown";
      if (!acc[month]) acc[month] = [];
      acc[month].push(task);
      return acc;
    }, {});
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Watermark />

      {/* Header */}
      <div className="flex items-center justify-between pb-6 mb-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 rounded-full bg-[#7AA33E]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage all tasks, priorities, and assignments</p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:scale-[0.98] transition shadow-sm"
          onClick={() => { setShowCreateForm(true); setCreateMsg(""); setAssigneeSearch(""); setNewTask({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "" }); }}
        >
          <span className="text-base leading-none">+</span> Create Org Task
        </button>
      </div>

      {/* Workflow + Dashboard Tabs */}
      <div className="flex flex-col gap-3 mb-5">
        {/* Workflow selector */}
        <div className="flex gap-1 bg-white rounded-2xl border border-gray-200 shadow-sm p-1.5">
          {WORKFLOW_TABS.map((t) => (
            <button
              key={t}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition ${
                workflowTab === t
                  ? "bg-[#23283a] text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              onClick={() => {
                sessionStorage.setItem("admin_workflow_tab", t);
                setWorkflowTab(t);
                // Default to open tasks on Management Requests, clear filter on Org Tasks
                setStatusFilter(t === "Management Requests" ? "open" : "");
                setPage(1);
              }}
            >
              {t === "Management Requests" ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  Management Requests
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Org Tasks
                </>
              )}
            </button>
          ))}
        </div>
      </div>{/* end Workflow Tabs */}

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
        {/* Card header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-800">
              {workflowTab === "Management Requests" ? "Management Requests" : "Org Tasks"}
              {statusFilter ? ` — ${{ due_today: "Due Today", overdue: "Overdue", open: "Open", in_progress: "Ongoing", blocked: "Blocked", on_hold: "On Hold", closed: "Closed" }[statusFilter] || statusFilter}` : ""}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-xl border border-gray-200 bg-gray-50 text-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by ID or Title"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 pl-9 pr-3 py-2 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
              <option value="due_today">Due Today</option>
              <option value="overdue">Overdue</option>
            </select>
            <button
              onClick={() => fetchTasks()}
              title="Refresh"
              className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Status Filter Cards */}
        <div className="overflow-x-auto mb-4">
          <StatusFilterCards
            stats={adminStats}
            activeFilter={statusFilter}
            onFilter={(f) => { setStatusFilter(f); setPage(1); }}
            variant="accent"
          />
        </div>

        {/* Tasks Table — single flat table */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full min-w-[1000px] text-sm text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
                <th className="py-3 px-4">#</th>
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4">Assigned To</th>
                <th className="py-3 px-4">Priority</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Due Date</th>
                <th className="py-3 px-4">Created</th>
                <th className="py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={8} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <div className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading tasks...</span>
                  </div>
                </td></tr>
              ) : filteredTasks.length === 0 ? (
                <tr><td colSpan={8} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span className="text-sm font-medium text-gray-500">No tasks found</span>
                  </div>
                </td></tr>
              ) : (
                paginatedTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                    <td className="py-3 px-4 text-gray-400 font-mono text-xs">{task.id}</td>
                    <td className="py-3 px-4 font-medium text-gray-800 max-w-[200px] truncate">{task.title}</td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                      {task.assignee_name?.trim() || <span className="text-gray-400 italic">Unassigned</span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        task.priority === "High"   ? "bg-red-100 text-red-700" :
                        task.priority === "Medium" ? "bg-yellow-100 text-yellow-700" :
                        "bg-green-100 text-green-700"
                      }`}>{task.priority}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                        task.status === "Open"    ? "bg-blue-100 text-blue-700" :
                        task.status === "Ongoing" ? "bg-yellow-100 text-yellow-700" :
                        task.status === "Blocked" ? "bg-red-100 text-red-700" :
                        task.status === "On Hold" ? "bg-orange-100 text-orange-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>{task.status}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                      {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                      {task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                    </td>
                    <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                      <button
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white font-semibold hover:bg-[#6a9335] transition"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 px-1">
          <div className="text-sm text-gray-600">
            Rows per page:{" "}
            <select
              value={rowsPerPage}
              onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              className="rounded border border-gray-300 bg-white text-gray-700 px-2 py-1 ml-1"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>Page {page} of {totalPages || 1}</span>
            <button
              className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >Prev</button>
            <button
              className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition disabled:opacity-40"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
            >Next</button>
          </div>
        </div>
      </div>{/* end Tasks Card */}

      {/* Create Org Task Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] border border-gray-100">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Create Organisation Task</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Asset collection, HR activities, documentation, etc.</p>
                </div>
              </div>
              <button
                disabled={taskCreating}
                onClick={() => { setShowCreateForm(false); setCreateMsg(""); setNewTask({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "" }); setAssigneeSearch(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition text-xl leading-none"
              >&times;</button>
            </div>

            {/* Modal Body */}
            <form id="admin-create-form" onSubmit={handleCreateAdminTask} className="flex flex-col gap-5 px-7 py-6 overflow-y-auto flex-1">

              {/* Title */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  value={newTask.title}
                  onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="e.g. Collect asset inventory"
                  disabled={taskCreating}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
                  required
                />
              </div>

              <div className="border-t border-gray-100" />

              {/* Description */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={newTask.description}
                  onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Describe the task in detail..."
                  rows={3}
                  disabled={taskCreating}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none disabled:opacity-50 transition"
                  required
                />
              </div>

              <div className="border-t border-gray-100" />

              {/* Priority */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</label>
                <div className="grid grid-cols-3 gap-2">
                  {PRIORITIES.map(p => (
                    <button key={p} type="button"
                      disabled={taskCreating}
                      onClick={() => setNewTask({ ...newTask, priority: p })}
                      className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition disabled:opacity-50 ${
                        newTask.priority === p
                          ? p === "High"   ? "border-red-400 bg-red-50 text-red-700"
                            : p === "Medium" ? "border-yellow-400 bg-yellow-50 text-yellow-700"
                            : "border-[#7AA33E] bg-[#f4f9ec] text-[#5a8a2a]"
                          : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
                      }`}
                    >{p}</button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* Assign To */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assign To</label>
                  <span className="text-xs text-gray-400">Optional</span>
                </div>
                {/* Search */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or employee ID..."
                    value={assigneeSearch}
                    disabled={taskCreating}
                    onChange={e => setAssigneeSearch(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent disabled:opacity-50 transition"
                  />
                </div>
                {/* Selected display */}
                {newTask.assigned_to && (() => {
                  const u = employeeList.find(e => String(e.id) === String(newTask.assigned_to));
                  return u ? (
                    <div className="flex items-center justify-between rounded-xl border border-[#7AA33E] bg-[#f4f9ec] px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#7AA33E] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{u.first_name} {u.last_name}</p>
                          {u.employee_id && <p className="text-xs text-gray-400">{u.employee_id}</p>}
                        </div>
                      </div>
                      <button type="button" onClick={() => { setNewTask({ ...newTask, assigned_to: "" }); setAssigneeSearch(""); }}
                        className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition text-sm">
                        &times;
                      </button>
                    </div>
                  ) : null;
                })()}
                {/* Filtered list — shown when search has text or nothing selected */}
                {!newTask.assigned_to && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto divide-y divide-gray-100">
                    {/* Unassigned option */}
                    <button type="button"
                      onClick={() => setNewTask({ ...newTask, assigned_to: "" })}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left bg-white hover:bg-gray-50 text-sm text-gray-400 italic transition"
                    >— Unassigned (assign later)</button>
                    {(() => {
                      const q = assigneeSearch.trim().toLowerCase();
                      const filtered = q
                        ? employeeList.filter(u =>
                            `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
                            (u.employee_id || '').toLowerCase().includes(q)
                          )
                        : employeeList;
                      if (filtered.length === 0)
                        return (
                          <div className="flex flex-col items-center py-5 text-gray-400">
                            <p className="text-xs">No users match your search</p>
                          </div>
                        );
                      const roleBadge = (u) => {
                        if (Number(u.role_id) === 2) return 'bg-blue-50 text-blue-600';
                        if (Number(u.role_id) === 4) return 'bg-yellow-50 text-yellow-600';
                        return 'bg-green-50 text-green-600';
                      };
                      const roleLabel = (u) => {
                        if (Number(u.role_id) === 2) return 'Admin';
                        if (Number(u.role_id) === 4) return 'Manager';
                        return 'Employee';
                      };
                      return filtered.map(u => (
                        <button key={u.id} type="button"
                          onClick={() => { setNewTask({ ...newTask, assigned_to: String(u.id) }); setAssigneeSearch(""); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left bg-white hover:bg-gray-50 transition"
                        >
                          <div className="w-7 h-7 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{u.first_name} {u.last_name}</p>
                            {u.employee_id && <p className="text-xs text-gray-400">{u.employee_id}</p>}
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${roleBadge(u)}`}>
                            {roleLabel(u)}
                          </span>
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100" />

              {/* Due Date */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</label>
                  <span className="text-xs text-gray-400">Optional — no past dates or Sundays</span>
                </div>
                <input type="date" value={newTask.due_date}
                  min={getMinDate()}
                  disabled={taskCreating}
                  onChange={e => {
                    const val = e.target.value;
                    const err = validateDueDate(val);
                    if (err) { setCreateMsg(err); return; }
                    setCreateMsg("");
                    setNewTask({ ...newTask, due_date: val });
                  }}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
                />
              </div>

              {/* Error message */}
              {createMsg && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  {createMsg}
                </div>
              )}

            </form>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 px-7 py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button type="submit" form="admin-create-form"
                disabled={taskCreating}
                className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
              >
                {taskCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Task"
                )}
              </button>
              <button type="button"
                disabled={taskCreating}
                onClick={() => { setShowCreateForm(false); setCreateMsg(""); setNewTask({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "" }); setAssigneeSearch(""); }}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 transition"
              >Cancel</button>
            </div>

          </div>
        </div>
      )}

      {/* ── Overall Export Modal ── */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
            {/* Header */}
            <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 leading-tight">Export Tasks</h3>
                <p className="text-xs text-gray-400 mt-0.5">Select a date range to export task data to Excel</p>
              </div>
              <button
                className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-7 py-5 flex flex-col gap-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                Exports all org tasks and management requests created within the selected period, including subtasks, comments, attachment metadata, reports, and audit history.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From</label>
                  <input
                    type="date"
                    value={exportFrom}
                    max={exportTo || undefined}
                    onChange={e => setExportFrom(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white transition"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To</label>
                  <input
                    type="date"
                    value={exportTo}
                    min={exportFrom || undefined}
                    onChange={e => setExportTo(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white transition"
                  />
                </div>
              </div>
              {exportFrom && exportTo && new Date(exportFrom) > new Date(exportTo) && (
                <p className="text-xs text-red-500 font-medium">⚠ "From" date cannot be after "To" date</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-7 py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                disabled={exporting || !exportFrom || !exportTo || new Date(exportFrom) > new Date(exportTo)}
                onClick={handleRunExport}
                className="flex-1 py-2.5 rounded-xl bg-[#23283a] text-white font-semibold text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {exporting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Exporting…</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default AdminDashboard;